import React from 'react';
import PropTypes from 'prop-types';
import { KEY_ENTER, USER_ROLES } from '@hobs/utils/constants';
import { GhostButton } from '@hobs/components/Buttons';
import { format } from '@hobs/utils/formatWithTimeZone';
import { Download } from '@hobs/utils/download';
import Icon from '@hobs/components/Icon';
import Toast from '@hobs/components/Toast';
import Confirm from '@hobs/components/Confirm';

import './Catalog.scss';

class Catalog extends React.PureComponent {
  state = {
    dataState: {
      take: 20,
      skip: 0,
      sort: [{ field: 'Code', dir: 'asc' }]
    },
    upload: {
      files: null,
      overrideItems: false,
      success: false,
      error: false
    },
    remove: {
      catalogItemCode: '',
      catalogItemId: '',
      showDialog: false,
      error: false
    }
  };

  componentDidMount() {
    this.props.getCatalogGrid();
    this.queryOData();
  }

  componentWillUnmount() {
    this.props.resetOData();
  }

  queryOData = () => {
    this.props.queryOData('CatalogItemQuery', this.state.dataState);
  };

  dataStateChange = event => {
    this.setState(
      {
        dataState: event.dataState
      },
      () => {
        this.queryOData();
      }
    );
  };

  exportCatalog = async () => {
    const data = await this.props.exportCatalog();
    const date = format(new Date(), 'yyyy-MM-dd');
    Download(data.blob, `Catalog-${date}.xlsx`);
  };

  getUplodaPathName() {
    return `${this.props.location.pathname.replace('/upload', '')}/upload`;
  }

  getCreatePathName() {
    return `${this.props.location.pathname.replace('/create', '')}/create`;
  }

  getEditPathName(catalogItemId) {
    return `${this.props.location.pathname.replace(
      `/edit/${catalogItemId}`,
      ''
    )}/edit/${catalogItemId}`;
  }

  getCatalogPathName() {
    const pathname = this.props.location.pathname;
    return pathname.replace('/upload', '');
  }

  isUploadOpen() {
    return this.props.match.params.action === 'upload';
  }

  showDeleteDialog = (catalogItemId, catalogItemCode) => {
    const { t } = this.props;
    Confirm(
      t('Delete catalog item'),
      t(`Are you sure you want to delete catalog item ${catalogItemCode}?`),
      () => {
        this.deleteCatalogItem(catalogItemId);
      }
    );
  };

  closeDeleteDialog = () => {
    const remove = {
      catalogItemId: '',
      catalogItemCode: '',
      showDialog: false,
      error: false
    };
    this.setState({ remove });
  };

  uploadCatalog = async () => {
    const { t } = this.props;
    const response = await this.props.uploadCatalog(this.state.upload);

    if (response.success) {
      await this.queryOData();

      this.setState({
        upload: {
          files: null,
          overrideItems: false,
          success: true,
          error: false
        }
      });

      this.onDialogClose();
      Toast.success(t('File uploaded successfully'));
    } else {
      this.setState({
        upload: {
          ...this.state.upload,
          error: response.payload
        }
      });
    }
  };

  deleteCatalogItem = catalogItemId => {
    const { t } = this.props;
    this.props.deleteCatalogItem(catalogItemId).then(response => {
      if (response.success) {
        Toast.success(t('Catalog item deleted successfully'));
        this.queryOData();
      } else {
        Toast.warning(response.payload.message);
      }
    });
  };

  handleQuickSearchChange = event => {
    if (event.keyCode === KEY_ENTER) {
      this.setState(
        {
          dataState: {
            ...this.state.dataState,
            skip: 0,
            filter: {
              logic: 'and',
              filters: [
                {
                  field: 'QuickSearchField',
                  operator: 'contains',
                  value: event.target.value
                }
              ]
            }
          }
        },
        () => {
          this.queryOData();
        }
      );
    }
  };

  handleInputChange = (input, value) => {
    this.setState({
      upload: {
        ...this.state.upload,
        [input]: value
      }
    });
  };

  getCustomFilter(type, field) {
    const { t, catalogGrid } = this.props;
    const filters = catalogGrid[type];

    if (!filters) {
      return [];
    }

    return [
      {
        name: t('Select all'),
        default: true,
        value: ''
      },
      ...filters.map(item => ({
        value: `<guid>${item.id}</guid>`,
        name: item.title,
        default: false,
        field: field
      }))
    ];
  }

  onDialogClose = () => {
    this.props.history.push(this.getCatalogPathName());
    this.setState({
      upload: {
        files: null,
        overrideItems: false,
        success: false,
        error: false
      }
    });
  };

  _getGridColumns() {
    const { t } = this.props;

    return {
      Code: {
        default: true,
        visible: true,
        title: t('Code'),
        columnMenu: true,
        width: '70px'
      },
      HasAttachments: {
        default: true,
        visible: true,
        cell: props => (
          <td colSpan="1">
            {props.dataItem.HasAttachments && (
              <Icon icon="attachment" className="catalog-view--icon" />
            )}
          </td>
        ),
        headerCell: () => (
          <Icon icon="attachment" className="catalog-view--icon" />
        )
      },
      ServiceName: {
        default: true,
        visible: true,
        title: t('Service Name'),
        columnMenu: true
      },
      ServiceType: {
        default: true,
        visible: true,
        title: t('Service type'),
        columnMenu: true,
        width: '80px',
        field: 'ServiceType',
        customFilter: {
          data: this.getCustomFilter('serviceTypes', 'ServiceTypeId')
        }
      },
      ServiceParent: {
        default: true,
        visible: true,
        title: t('Service parent'),
        columnMenu: true
      },
      ServiceDescription: {
        default: true,
        visible: true,
        title: t('Service Description'),
        columnMenu: true
      },
      GroupName: {
        default: true,
        visible: true,
        title: t('Group'),
        columnMenu: true,
        customFilter: {
          data: this.getCustomFilter('groups', 'GroupId')
        }
      },
      AnnexName: {
        default: true,
        visible: true,
        title: t('Annex'),
        columnMenu: true,
        customFilter: {
          data: this.getCustomFilter('annexes', 'AnnexId')
        }
      },
      Uoms: {
        default: true,
        visible: true,
        title: t('UoM'),
        sortable: false,
        cell: props => {
          return (
            <td colSpan="1">
              {props.dataItem.Uoms &&
                this.getUomFieldValue(props.dataItem.Uoms)}
            </td>
          );
        }
      },
      ServiceNatureType: {
        default: true,
        visible: true,
        title: t('Service nature'),
        columnMenu: true,
        customFilter: {
          data: this.getCustomFilter('serviceNatures', 'ServiceNatureTypeId')
        }
      },
      RateFrom: {
        default: true,
        visible: true,
        title: t('Rate from '),
        columnMenu: true,
        format: '{0:n2}',
        filter: 'numeric'
      },
      RateTo: {
        default: true,
        visible: true,
        title: t('Rate to'),
        columnMenu: true,
        format: '{0:n2}',
        filter: 'numeric'
      },
      ValueAddedTaxCode: {
        default: true,
        visible: true,
        title: t('VAT code'),
        columnMenu: true,
        customFilter: {
          data: this.getCustomFilter('vatCodes', 'ValueAddedTaxId')
        }
      }
    };
  }

  getGridColumns() {
    const { t, hasUserPermission } = this.props;
    const columns = this._getGridColumns();

    if (
      !hasUserPermission(USER_ROLES.READONLY) &&
      !hasUserPermission(USER_ROLES.HOST_UNIT)
    ) {
      Object.assign(columns, {
        edit: {
          default: true,
          visible: true,
          resizable: false,
          width: '110px',
          headerCell: () => <></>,
          cell: props => (
            <td className="catalog-view--button--column" colSpan="1">
              <GhostButton
                iconLeft={<Icon icon="Pen" />}
                to={this.getEditPathName(props.dataItem.CatalogItemId)}
              >
                {t('Edit')}
              </GhostButton>
            </td>
          )
        },
        delete: {
          default: true,
          visible: true,
          resizable: false,
          width: '116px',
          headerCell: () => <></>,
          cell: props => (
            <td className="catalog-view--button--column" colSpan="1">
              <GhostButton
                iconLeft={<Icon icon="Delete" />}
                onClick={() =>
                  this.showDeleteDialog(
                    props.dataItem.CatalogItemId,
                    props.dataItem.Code
                  )
                }
              >
                {t('Delete')}
              </GhostButton>
            </td>
          )
        }
      });
    }
    return columns;
  }

  getUomFieldValue = uomsValues => {
    if (!uomsValues || uomsValues.length === 0) {
      return '';
    }

    if (!Array.isArray(uomsValues)) {
      throw new Error('Given value (Uoms) is not an Array. (CatalogItemQuery)');
    }

    const defaultUom = uomsValues.find(item => item.IsDefault);
    const restUoms = uomsValues.filter(item => !item.IsDefault);
    const uomsString = restUoms.map(item => item.UnitOfMeasureCode).join('; ');

    return (
      <>
        <span className="default-uom">{defaultUom.UnitOfMeasureCode}; </span>
        {uomsString}
      </>
    );
  };
}

Catalog.propTypes = {
  isLoading: PropTypes.bool,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  upload: PropTypes.bool,
  oData: PropTypes.object,
  queryOData: PropTypes.func,
  resetOData: PropTypes.func,
  exportCatalog: PropTypes.func,
  uploadCatalog: PropTypes.func,
  deleteCatalogItem: PropTypes.func,
  t: PropTypes.func,
  hasUserPermission: PropTypes.func,
  getCatalogGrid: PropTypes.func,
  catalogGrid: PropTypes.object,
  user: PropTypes.object
};

export default Catalog;
